import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle, XCircle, Mail } from "lucide-react";
import axios, { getApiUrl } from "@/lib/api";

const API = `${getApiUrl()}/api`;

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [status, setStatus] = useState("loading"); // loading, done, error
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus("error");
      return;
    }
    unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const unsubscribe = async () => {
    try {
      const res = await axios.post(`${API}/community/newsletter/unsubscribe`, { token });
      setEmail(res.data.email || "");
      setSubscribed(false);
      setStatus("done");
    } catch (error) {
      setStatus("error");
    }
  };

  const resubscribe = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/community/newsletter/resubscribe`, { token });
      setEmail(res.data.email || email);
      setSubscribed(true);
    } catch (error) {
      // ignore
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4" data-testid="unsubscribe-page">
      <div className="w-full max-w-md text-center">
        <div className="inline-flex items-center justify-center mb-6">
          <img src="/logo.png" alt="Game-o-Rang" className="w-24 h-24 object-contain" />
        </div>

        <div className="inline-flex items-center justify-center mb-6">
          {status === "loading" && (
            <div className="w-16 h-16 rounded-full bg-purple-500/10 flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
            </div>
          )}
          {status === "done" && (
            <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          )}
          {status === "error" && (
            <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center">
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          )}
        </div>

        <h1 className="text-2xl font-bold text-white mb-4">
          {status === "loading" && "Updating your preferences…"}
          {status === "done" && (subscribed ? "You're subscribed again" : "You've been unsubscribed")}
          {status === "error" && "Link not valid"}
        </h1>

        <p className="text-white/60 mb-8" data-testid="unsubscribe-message">
          {status === "done" && !subscribed && (
            <>
              {email ? <strong className="text-white/80">{email}</strong> : "You"} will no longer receive
              community newsletter emails. Changed your mind?
            </>
          )}
          {status === "done" && subscribed && (
            <>You're back on the list and will get community news emails again.</>
          )}
          {status === "error" && "This unsubscribe link is invalid or has expired. You can manage newsletter emails from Settings after signing in."}
        </p>

        <div className="space-y-4">
          {status === "done" && !subscribed && (
            <Button
              onClick={resubscribe}
              disabled={busy}
              data-testid="resubscribe-btn"
              className="w-full bg-purple-600 hover:bg-purple-700"
            >
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
              Resubscribe
            </Button>
          )}
          <Link to="/login">
            <Button variant="ghost" className="w-full text-purple-400 hover:text-purple-300">
              Back to Game-o-Rang
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
